import { inject, Injectable } from '@angular/core';
import { Account, AccountConnections, AccountService, tiers } from './account.service';
import { ApiService } from './api.service';
import { Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { Character, Backdrop, Script, Suggestion } from './script.service';
import { diffChars, diffLines, diffWords } from 'diff';
export type DiffGranularity = 'char' | 'word' | 'line';

export interface KnowledgePage {
  id: number;
  name: string;
  description: string;
  content: string;
  published: boolean;
  updated_at: string;
}

export interface AdminScript extends Script {
  flags: number;
  versions?: {
    version: number;
    title: string;
    content: string;
    status: number;
    created_at: string;
    is_draft: boolean;
    author_comment?: string;
    staff_comment?: string;
  }[];
  viewCount?: number;
  ratingAverage?: number;
  ratingCount?: number;
}

/*
Status values:
0: No status (superseeded while pending)
1: Pending (Newly submitted, not reviewed yet)
4: Rejected
5: Rejected, Pending update
8: Published
9: Published, Pending update

Published (Bit 8) takes priority over other statuses.
Published is meant to be used for versions that are currently live.
*/

@Injectable({
  providedIn: 'root'
})
export class AdminService {

  apiService = inject(ApiService);

  accountService = inject(AccountService);
  totalUserCount = 100;
  totalPatreonCount = 0;

  readonly lastPage = 5;

  fetchUsers(search: string, page: number, pageSize: number): Observable<Account[]> {
    return this.apiService.get(`admin/fetchUsers.php?search=${search}&page=${page}&page_lenght=${pageSize}`).pipe(
      map((data: {users: Account[], total_users: number}) => {
        this.totalUserCount = data.total_users;

        let users = [];
        for (let user of data.users) {
          if (user.connections.patreon) {
            user.connections.patreon.subscriptionTier = tiers[user.connections.patreon.subscriptionTier as any];
          }
          users.push(user);
        }

        return users;
      })
    );
  }

  getUser(userID: string): Observable<Account> {
    return this.apiService.get(`admin/getUser.php?user_id=${userID}`).pipe(
      map((data: {user: Account, code: number}) => {
        if (data.user.connections.patreon) {
          data.user.connections.patreon.subscriptionTier = tiers[data.user.connections.patreon.subscriptionTier as any];
        }
        return data.user;
      })
    );
  }

  checkVRChatUsername(userID: string): Observable<any> {
    return this.apiService.get(`admin/checkVRChatUsername.php?user_id=${userID}`).pipe(
      map((data: any) => {
        return data;
      })
    );
  }

  updateVRChatUsername(userID: string): Observable<any> {
    return this.apiService.get(`admin/updateVRChatUsername.php?user_id=${userID}`).pipe(
      map((data: any) => {
        return data;
      })
    );
  }

  patchUser(user: Account): Observable<any> {
    return this.apiService.post(`admin/patchUser.php`, {data: user});
  }

  getScript(script_id: number, version?: number): Observable<AdminScript|null> {
    const url = version 
      ? `admin/fetchScript.php?script_id=${script_id}&version=${version}`
      : `admin/fetchScript.php?script_id=${script_id}`;
    
    return this.apiService.get(url).pipe(
      map((data: {data: AdminScript|null, code: number}) => {
        return data.data;
      })
    );
  }

  uploadScripts(): Observable<any> {
    return this.apiService.get(`cron/script_upload.php`);
  }

  performVersionAction(script_id: number, version: number, action: 'reject' | 'publish'): Observable<any> {
    return this.apiService.post(`admin/versionActions.php`, {
      script_id,
      version,
      action
    });
  }

  getScripts(
    query: string,
    page: number,
    filters: number = 0,
    includeDrafts: boolean = false,
    sortBy: string = 'id',
    sortOrder: string = 'desc'
  ): Observable<{ data: { scripts: AdminScript[]; total_scripts: number; counts: { pending: number; pending_update: number; rejected: number; published: number; previously_published: number } }; code: number; page_size: number }> {
    return this.apiService.get(
      `admin/fetchScripts.php?query=${query}&page=${page}&filters=${filters}&include_drafts=${includeDrafts}&sort_by=${sortBy}&sort_order=${sortOrder}`
    );
  }

  saveStaffComment(script_id: number, version: number, comment: string): Observable<any> {
    return this.apiService.post(`admin/saveStaffComment.php`, {
      "script_id": script_id,
      "version": version,
      "comment": comment
    });
  }

  getAIRating(script_id: number, version: number): Observable<any> {
    return this.apiService.get(`admin/scriptRating.php?script_id=${script_id}&version=${version}`).pipe(
      map((data: any) => {
        return data;
      })
    );
  }

  // Gamenight management methods
  getGamenightOptions(): Observable<any[]> {
    return this.apiService.get(`admin/gamenightOptions.php`).pipe(
      map((response: any) => {
        // The API returns {code: 200, msg: "OK", data: [...]}
        return response.data || [];
      })
    );
  }

  manageGamenightOption(action: 'create' | 'update' | 'delete' | 'reset_votes', data?: any): Observable<any> {
    return this.apiService.post(`admin/gamenightOptions.php`, {
      action,
      ...data
    }).pipe(
      map((response: any) => {
        // The API returns {code: 200, msg: "OK", message: "...", ...}
        return response;
      })
    );
  }

  // Stats management methods
  getStats(): Observable<any> {
    return this.apiService.get(`admin/stats.php`).pipe(
      map((response: any) => {
        // The API returns {code: 200, msg: "OK", stats: {...}}
        return response.stats || {};
      })
    );
  }

  manageStatDefinition(action: 'create' | 'update' | 'delete' | 'reset', data: any): Observable<any> {
    return this.apiService.post(`admin/stats.php`, {
      action,
      ...data
    }).pipe(
      map((response: any) => {
        // The API returns {code: 200, msg: "OK", message: "...", ...}
        return response;
      })
    );
  }

  // Cult Bot Knowledge management methods
  getKnowledgePages(): Observable<KnowledgePage[]> {
    return this.apiService.get(`admin/knowledgeBase.php`).pipe(
      map((response: any) => {
        // The API returns {code: 200, data: [...]}
        return response.data || [];
      })
    );
  }

  createKnowledgePage(page: { name: string; description: string; content: string; published: boolean }): Observable<any> {
    return this.apiService.post(`admin/knowledgeBase.php`, {
      action: 'create',
      ...page
    });
  }

  updateKnowledgePage(page: { id: number; name: string; description: string; content: string; published: boolean }): Observable<any> {
    return this.apiService.post(`admin/knowledgeBase.php`, {
      action: 'update',
      ...page
    });
  }

  deleteKnowledgePage(id: number): Observable<any> {
    return this.apiService.post(`admin/knowledgeBase.php`, {
      action: 'delete',
      id
    });
  }

  getDiff(old: Script, updated: Script, mode: DiffGranularity = 'char'): AdminScriptDiff {
    const diff: AdminScriptDiff = {
      title: {
        old: old.title,
        updated: updated.title,
        changed: old.title !== updated.title
      },
      categories: {
        old: old.categories,
        updated: updated.categories,
        changed: JSON.stringify(old.categories.sort()) !== JSON.stringify(updated.categories.sort())
      },
      suggestions: {
        old: old.suggestions,
        updated: updated.suggestions,
        changed: JSON.stringify(old.suggestions) !== JSON.stringify(updated.suggestions),
        diff: this.getArrayDiff(old.suggestions, updated.suggestions, 'tag')
      },
      characters: {
        old: old.characters,
        updated: updated.characters,
        changed: JSON.stringify(old.characters) !== JSON.stringify(updated.characters),
        diff: this.getArrayDiff(old.characters, updated.characters, 'tag')
      },
      backdrop: {
        old: old.backdrop,
        updated: updated.backdrop,
        changed: old.backdrop.id !== updated.backdrop.id
      },
      lines: this.getContentDiff(old.content, updated.content, mode)
    };
    
    return diff;
  }

  private getContentDiff(oldContent: string, updatedContent: string, mode: DiffGranularity): { content: string, type: "added" | "removed" | "changed" | "unchanged", html?: string }[] {
    // Helper to split by lines without adding trailing empties
    const splitLines = (text: string): string[] => {
      const arr = text.split('\n');
      if (text.endsWith('\n')) arr.pop();
      return arr;
    };

    // Escape HTML in text fragments before composing innerHTML
    const escapeHtml = (text: string): string => {
      return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\"/g, '&quot;')
        .replace(/'/g, '&#39;');
    };

    const result: { content: string, type: "added" | "removed" | "changed" | "unchanged", html?: string }[] = [];

    // First, compute a line-level diff so we do not introduce artificial new lines
    const lineParts = diffLines(oldContent, updatedContent);

    for (let i = 0; i < lineParts.length; i++) {
      const part = lineParts[i];

      if (part.removed && i + 1 < lineParts.length && lineParts[i + 1].added) {
        // Pair removed with following added to mark them as changed, depending on granularity
        const removedLines = splitLines(part.value);
        const addedLines = splitLines(lineParts[i + 1].value);
        const maxLen = Math.max(removedLines.length, addedLines.length);

        for (let k = 0; k < maxLen; k++) {
          const removedLine = k < removedLines.length ? removedLines[k] : null;
          const addedLine = k < addedLines.length ? addedLines[k] : null;

          if (mode === 'line') {
            if (removedLine !== null) {
              const safe = escapeHtml(removedLine);
              result.push({ content: removedLine, type: 'removed', html: `<span class="removed">${safe}</span>` });
            }
            if (addedLine !== null) {
              const safe = escapeHtml(addedLine);
              result.push({ content: addedLine, type: 'added', html: `<span class="added">${safe}</span>` });
            }
          } else if (removedLine !== null && addedLine !== null) {
            const parts = mode === 'word' ? diffWords(removedLine, addedLine) : diffChars(removedLine, addedLine);
            let html = '';
            let plain = '';
            for (const cp of parts as any[]) {
              const safeVal = escapeHtml(cp.value);
              if (cp.added) {
                html += `<span class="added">${safeVal}</span>`;
              } else if (cp.removed) {
                html += `<span class="removed">${safeVal}</span>`;
              } else {
                html += safeVal; // unchanged
              }
              plain += cp.value;
            }
            result.push({ content: plain, type: 'changed', html });
          } else if (addedLine !== null) {
            const safe = escapeHtml(addedLine);
            result.push({ content: addedLine, type: 'added', html: `<span class="added">${safe}</span>` });
          } else if (removedLine !== null) {
            const safe = escapeHtml(removedLine);
            result.push({ content: removedLine, type: 'removed', html: `<span class="removed">${safe}</span>` });
          }
        }

        // Skip the paired added part
        i++;
        continue;
      }

      if (part.added) {
        for (const line of splitLines(part.value)) {
          result.push({ content: line, type: 'added', html: `<span class="added">${escapeHtml(line)}</span>` });
        }
        continue;
      }

      if (part.removed) {
        for (const line of splitLines(part.value)) {
          result.push({ content: line, type: 'removed', html: `<span class="removed">${escapeHtml(line)}</span>` });
        }
        continue;
      }

      // Unchanged
      for (const line of splitLines(part.value)) {
        result.push({ content: line, type: 'unchanged', html: `<span class="unchanged">${escapeHtml(line)}</span>` });
      }
    }

    return result;
  }

  private getArrayDiff<T extends { [key: string]: any }>(oldArray: T[], newArray: T[], keyField: keyof T): {
    added: T[];
    removed: T[];
    unchanged: T[];
    modified: { old: T; new: T }[];
  } {
    const oldMap = new Map<string, T>();
    const newMap = new Map<string, T>();
    
    // Create maps for easy lookup
    oldArray.forEach(item => {
      const key = String(item[keyField]);
      oldMap.set(key, item);
    });
    
    newArray.forEach(item => {
      const key = String(item[keyField]);
      newMap.set(key, item);
    });
    
    const added: T[] = [];
    const removed: T[] = [];
    const unchanged: T[] = [];
    const modified: { old: T; new: T }[] = [];
    
    // Find added items (not in old array)
    newArray.forEach(newItem => {
      const newKey = String(newItem[keyField]);
      if (!oldMap.has(newKey)) {
        // Check if this might be a renamed item by comparing other properties
        const isRenamed = oldArray.some(oldItem => {
          // For characters, compare name and location
          if ('name' in oldItem && 'name' in newItem && 'location' in oldItem && 'location' in newItem) {
            return oldItem['name'] === newItem['name'] && 
                   JSON.stringify(oldItem['location']) === JSON.stringify(newItem['location']);
          }
          // For suggestions, compare description and examples
          if ('description' in oldItem && 'description' in newItem && 'examples' in oldItem && 'examples' in newItem) {
            return oldItem['description'] === newItem['description'] && 
                   JSON.stringify(oldItem['examples']) === JSON.stringify(newItem['examples']);
          }
          return false;
        });
        
        if (!isRenamed) {
          added.push(newItem);
        }
      }
    });
    
    // Find removed items (not in new array)
    oldArray.forEach(oldItem => {
      const oldKey = String(oldItem[keyField]);
      if (!newMap.has(oldKey)) {
        // Check if this might be a renamed item by comparing other properties
        const isRenamed = newArray.some(newItem => {
          // For characters, compare name and location
          if ('name' in oldItem && 'name' in newItem && 'location' in oldItem && 'location' in newItem) {
            return oldItem['name'] === newItem['name'] && 
                   JSON.stringify(oldItem['location']) === JSON.stringify(newItem['location']);
          }
          // For suggestions, compare description and examples
          if ('description' in oldItem && 'description' in newItem && 'examples' in oldItem && 'examples' in newItem) {
            return oldItem['description'] === newItem['description'] && 
                   JSON.stringify(oldItem['examples']) === JSON.stringify(newItem['examples']);
          }
          return false;
        });
        
        if (!isRenamed) {
          removed.push(oldItem);
        }
      }
    });
    
    // Find modified items (renamed or changed content)
    oldArray.forEach(oldItem => {
      const oldKey = String(oldItem[keyField]);
      const newItem = newMap.get(oldKey);
      
      if (newItem) {
        // Same key exists, check if content changed
        if (JSON.stringify(oldItem) !== JSON.stringify(newItem)) {
          modified.push({ old: oldItem, new: newItem });
        } else {
          unchanged.push(oldItem);
        }
      } else {
        // Check if this old item was renamed to a new item
        const renamedTo = newArray.find(newItem => {
          // For characters, compare name and location
          if ('name' in oldItem && 'name' in newItem && 'location' in oldItem && 'location' in newItem) {
            return oldItem['name'] === newItem['name'] && 
                   JSON.stringify(oldItem['location']) === JSON.stringify(newItem['location']);
          }
          // For suggestions, compare description and examples
          if ('description' in oldItem && 'description' in newItem && 'examples' in oldItem && 'examples' in newItem) {
            return oldItem['description'] === newItem['description'] && 
                   JSON.stringify(oldItem['examples']) === JSON.stringify(newItem['examples']);
          }
          return false;
        });
        
        if (renamedTo) {
          modified.push({ old: oldItem, new: renamedTo });
        }
      }
    });
    
    return { added, removed, unchanged, modified };
  }
}

export interface AdminScriptDiff {
  title: {
    old: string;
    updated: string;
    changed: boolean;
  };
  categories: {
    old: string[];
    updated: string[];
    changed: boolean;
  };
  suggestions: {
    old: Suggestion[];
    updated: Suggestion[];
    changed: boolean;
    diff: {
      added: Suggestion[];
      removed: Suggestion[];
      unchanged: Suggestion[];
      modified: { old: Suggestion; new: Suggestion }[];
    };
  };
  characters: {
    old: Character[];
    updated: Character[];
    changed: boolean;
    diff: {
      added: Character[];
      removed: Character[];
      unchanged: Character[];
      modified: { old: Character; new: Character }[];
    };
  };
  backdrop: {
    old: Backdrop;
    updated: Backdrop;
    changed: boolean;
  };
  lines: {
    content: string,
    type: "added" | "removed" | "changed" | "unchanged",
    html?: string,
  }[];
}
